/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { X, Delete, UserCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import api from '../../services/api';

interface POSOverrideModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    actionName?: string;
    requiredPermission?: string;
    restaurantId: string;
}

export default function POSOverrideModal({
    isOpen,
    onClose,
    onSuccess,
    actionName,
    requiredPermission,
    restaurantId
}: POSOverrideModalProps) {
    const [pinCode, setPinCode] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset state on open
    useEffect(() => {
        if (isOpen) {
            setPinCode('');
        }
    }, [isOpen]);

    // Add keyboard listener for RFID scanner (acts as keyboard)
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Only capture numbers if they come from the keyboard
            if (e.key >= '0' && e.key <= '9') {
                if (pinCode.length < 20) {
                    setPinCode(prev => prev + e.key);
                }
            } else if (e.key === 'Backspace') {
                handleDelete();
            } else if (e.key === 'Enter') {
                if (pinCode.length >= 4) {
                    handleSubmit();
                }
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, pinCode]);

    const handleNumberClick = (num: string) => {
        if (pinCode.length < 20) {
            setPinCode(prev => prev + num);
        }
    };

    const handleDelete = () => {
        setPinCode(prev => prev.slice(0, -1));
    };

    const handleClear = () => {
        setPinCode('');
    };

    const handleSubmit = async () => {
        if (pinCode.length < 4) {
            toast.error("Le code PIN doit comporter au moins 4 chiffres");
            return;
        }

        try {
            setLoading(true);

            // Fetch the POS context if available to enforce terminal restrictions for the overriding user too
            const posIdStr = localStorage.getItem('pos_context_id');
            const headers: any = {};
            if (posIdStr) {
                headers['X-POS-ID'] = posIdStr;
            }

            let response;
            try {
                // Attempt PIN direct first
                response = await api.post('/employees/auth/auth/pin-direct', {
                    restaurantId: Number(restaurantId),
                    pin: pinCode
                }, { headers });
            } catch (pinError: any) {
                // If PIN fails (401 or 404), try Badge/RFID
                if (pinError.response?.status === 401 || pinError.response?.status === 404) {
                    try {
                        response = await api.post('/employees/auth/auth/badge', {
                            restaurantId: Number(restaurantId),
                            badgeId: pinCode
                        }, { headers });
                    } catch (badgeError) {
                        // If badge also fails, throw the original PIN error for UI display
                        throw pinError;
                    }
                } else {
                    throw pinError;
                }
            }

            const user = response.data.data.user;

            // Check if the authenticated user has the required permission
            // Or if they are super_admin, admin, manager, partner
            const role = user.role;
            const hasRoleAccess = ['super_admin', 'admin', 'manager', 'partner'].includes(role);
            const hasSpecificPermission = requiredPermission && user.permissions?.includes(requiredPermission);

            if (hasRoleAccess || hasSpecificPermission) {
                toast.success(`Autorisation accordée (${user.first_name || role})`);
                onSuccess();
                onClose();
            } else {
                toast.error(`L'employé ${user.first_name || ''} n'a pas les droits requis pour cette action`);
                setPinCode('');
            }
        } catch (error: any) {
            console.error('Erreur override:', error);
            if (error.response?.status === 401 || error.response?.status === 404) {
                toast.error("Code ou Badge non reconnu");
            } else if (error.response?.status === 403) {
                toast.error("Employé non autorisé sur cette caisse");
            } else {
                toast.error("Erreur de connexion");
            }
            setPinCode(''); // Reset on error
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-5 border-b flex justify-between items-center bg-gray-50">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <UserCheck className="text-blue-600" />
                            Autorisation Requise
                        </h2>
                        {actionName && (
                            <p className="text-sm text-gray-500 mt-1">Action: {actionName}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors self-start"
                        disabled={loading}
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    <p className="text-center text-sm text-gray-600 mb-6">
                        Veuillez saisir un code PIN Manager ou d'un employé autorisé
                    </p>

                    {/* PIN Display */}
                    <div className="flex justify-center gap-3 mb-8">
                        {pinCode.length <= 6 ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={clsx(
                                        "w-4 h-4 rounded-full border-2 transition-all",
                                        i < pinCode.length
                                            ? "bg-blue-600 border-blue-600 scale-110"
                                            : "border-gray-300"
                                    )}
                                />
                            ))
                        ) : (
                            <div className="text-blue-600 font-medium animate-pulse flex items-center gap-2">
                                <UserCheck size={18} />
                                Badge scanné ({pinCode.length} car.)
                            </div>
                        )}
                    </div>

                    {/* Keypad */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                            <button
                                key={num}
                                onClick={() => handleNumberClick(num.toString())}
                                disabled={loading || pinCode.length >= 20}
                                className="h-16 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border text-2xl font-semibold flex items-center justify-center transition-colors disabled:opacity-50"
                            >
                                {num}
                            </button>
                        ))}
                        <button
                            onClick={handleClear}
                            disabled={loading || pinCode.length === 0}
                            className="h-16 rounded-xl bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 font-medium flex items-center justify-center transition-colors disabled:opacity-50"
                        >
                            C
                        </button>
                        <button
                            onClick={() => handleNumberClick('0')}
                            disabled={loading || pinCode.length >= 6}
                            className="h-16 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border text-2xl font-semibold flex items-center justify-center transition-colors disabled:opacity-50"
                        >
                            0
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={loading || pinCode.length === 0}
                            className="h-16 rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 flex items-center justify-center transition-colors disabled:opacity-50"
                        >
                            <Delete size={24} className="text-gray-600" />
                        </button>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={loading || pinCode.length < 4}
                        className="w-full h-14 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-xl font-bold flex items-center justify-center transition-colors disabled:opacity-50"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                        ) : (
                            'Valider'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
